<script setup>
// The real Clubhouse TV screen, running on demo data, inside a marketing page.
// A 1920×1080 stage (the size a screen is designed at) scaled to fit the
// container, exactly as the tv layout scales it to a wall screen. Controls let
// a visitor flip the theme, the style and the accent, and fire a result sting,
// which is what makes it a demo rather than a screenshot.
import TvScreen from '~/components/tv/TvScreen.vue';
import {tvDemoCelebration, tvDemoView} from '~/utils/tvDemo';

const props = defineProps({
  // show the theme / style / accent / sting controls under the screen
  controls: {type: Boolean, default: true},
});

const theme = ref(1);
const style = ref(0);
const accent = ref('#D32946');
const ACCENTS = [
  {hex: '#D32946', name: 'Tap Leagues red'},
  {hex: '#1E9E76', name: 'Green'},
  {hex: '#2477C8', name: 'Blue'},
  {hex: '#DE870A', name: 'Amber'},
  {hex: '#7C3AED', name: 'Purple'},
  {hex: '#14181F', name: 'Ink'},
];

// Rebuilt when a control changes: the screen treats it like a refetch and
// stays on the panel it is showing.
const view = computed(() => tvDemoView({theme: theme.value, style: style.value, accentColour: accent.value}));

const screen = ref(null);
const confettiCanvas = ref(null);
const celebrate = () => {
  const {result, confirmed} = tvDemoCelebration();
  screen.value?.celebrate({...result, fixtureId: `${result.fixtureId}-${Date.now()}`}, {confirmed});
};

// Scale the stage to the width we are given
const STAGE_W = 1920;
const STAGE_H = 1080;
const host = ref(null);
const scale = ref(0.5);
let observer = null;
const fit = () => {
  if (host.value) scale.value = host.value.clientWidth / STAGE_W;
};
onMounted(() => {
  fit();
  observer = new ResizeObserver(fit);
  observer.observe(host.value);
});
onUnmounted(() => observer?.disconnect());

// The broadcast style's condensed numerals
useHead({
  link: [
    {rel: 'preconnect', href: 'https://fonts.googleapis.com'},
    {rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: ''},
    {rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800&display=swap'},
  ],
});
</script>

<template>
  <div class="tvd">
    <div class="tvd-bezel">
      <div ref="host" class="tvd-host" :style="{height: `${STAGE_H * scale}px`}">
        <div class="tvd-stage" :style="{width: `${STAGE_W}px`, height: `${STAGE_H}px`, transform: `scale(${scale})`}">
          <client-only>
            <tv-screen ref="screen" :view="view" :connected="true" :portrait="false" :confetti-canvas="confettiCanvas"/>
          </client-only>
        </div>
        <canvas ref="confettiCanvas" class="tvd-confetti"/>
      </div>
    </div>
    <div v-if="controls" class="tvd-controls">
      <div class="tl-seg" role="group" aria-label="Theme">
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': theme === 0}" @click="theme = 0">Light</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': theme === 1}" @click="theme = 1">Dark</button>
      </div>
      <div class="tl-seg" role="group" aria-label="Style">
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': style === 0}" @click="style = 0">Broadcast</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': style === 1}" @click="style = 1">Clubhouse</button>
      </div>
      <div class="tvd-swatches" role="group" aria-label="Accent colour">
        <button
            v-for="swatch in ACCENTS"
            :key="swatch.hex"
            type="button"
            class="tvd-swatch"
            :class="{'tvd-swatch--active': accent === swatch.hex}"
            :style="{background: swatch.hex}"
            :aria-label="swatch.name"
            :title="swatch.name"
            @click="accent = swatch.hex"/>
      </div>
      <v-btn variant="outlined" size="small" rounded="pill" prepend-icon="mdi-party-popper" @click="celebrate">
        Enter a result
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tvd-bezel {
  border-radius: 14px;
  padding: 10px;
  background: #0b0d11;
  box-shadow:
      0 40px 80px -40px rgba(0, 0, 0, 0.6),
      inset 0 0 0 1px rgba(255, 255, 255, 0.06);

  @media (min-width: 760px) {
    border-radius: 18px;
    padding: 14px;
  }
}

.tvd-host {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: #0f1b2a;
}

.tvd-stage {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  font-family: 'Poppins', sans-serif;
  /* The scaled stage is a picture of a screen: nothing in it is clickable */
  pointer-events: none;
  user-select: none;
}

/* Confetti stays inside the frame */
.tvd-confetti {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.tvd-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-top: 14px;
}

.tvd-swatches {
  display: inline-flex;
  gap: 6px;
}

.tvd-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid rgb(var(--v-theme-surface));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-border));
  cursor: pointer;
  transition: transform 0.12s ease;

  &:hover {
    transform: scale(1.1);
  }

  &--active {
    box-shadow: 0 0 0 2px rgb(var(--v-theme-text));
  }
}
</style>
